/**
 * Sign-in state (web.md §2 `session.ts`; sync.md "Host duties").
 *
 * Works over any object with the Engine's `plan`, `settings` and `sync` areas, so the same code runs
 * in the UI (through the EngineClient: the sign-in screen) and inside the engine worker (boot, timers,
 * coming online). The session itself is the httpOnly cookie; only `local_settings.account` (for
 * display and for sync's account check) and the cached entitlement live on the device.
 *
 * Order on sign-in (sync.md "Host duties"): `local_settings.account` → `plan.setEntitlement` →
 * `sync.start` when the plan has sync.
 */

import {
  DigesterError, toDigesterError, type AccountSummary, type ApiCaller, type DigesterErrorShape, type Engine, type Entitlement,
  type MeResponse, type OkResponse, type RequestCodeRequest, type SetPasswordRequest, type SetPasswordResponse, type SignInRequest,
  type SignInResponse, type VerifyCodeRequest, type VerifyCodeResponse,
} from '@digester/core';

export interface SessionDeps {
  engine: Pick<Engine, 'plan' | 'settings' | 'sync'>;
  api: ApiCaller;
  /** Name this device registers under (Account → Sync shows it). */
  deviceName: string;
  /** Current time; default Date.now. */
  now?: () => number;
}

/** What happened to sync after an entitlement was applied. */
export type SyncOutcome =
  | { status: 'off' }
  | { status: 'started' }
  /** This database was last synced with another account: ask the user (sync.start 'merge' | 'replace'). */
  | { status: 'conflict'; error: DigesterErrorShape }
  | { status: 'device_limit'; error: DigesterErrorShape }
  | { status: 'error'; error: DigesterErrorShape };

export interface SessionState {
  account: AccountSummary | null;
  sync: SyncOutcome;
}

const shape = (e: unknown): DigesterErrorShape => toDigesterError(e).toShape();

/** True when the entitlement is a paid plan whose period has not ended. */
export function isActivePaid(entitlement: Entitlement | null, now: number): boolean {
  return entitlement !== null && entitlement.plan !== 'free' && (entitlement.validUntil === null || entitlement.validUntil > now);
}

function syncAllowed(entitlement: Entitlement | null, now: number): boolean {
  return entitlement !== null && entitlement.limits.syncDevices > 0 && (entitlement.plan === 'free' || isActivePaid(entitlement, now));
}

export async function requestCode(api: ApiCaller, email: string): Promise<void> {
  const body: RequestCodeRequest = { email: email.trim() };
  await api.call<OkResponse>('requestCode', { body });
}

/** Register this device and start syncing; never throws for the expected refusals. */
export async function startSync(deps: SessionDeps, onOtherAccount?: 'merge' | 'replace'): Promise<SyncOutcome> {
  try {
    await deps.engine.sync.start(deps.deviceName, onOtherAccount);
    return { status: 'started' };
  } catch (error) {
    const e = toDigesterError(error);
    if (e.code === 'conflict') return { status: 'conflict', error: e.toShape() };
    if (e.code === 'device_limit') return { status: 'device_limit', error: e.toShape() };
    return { status: 'error', error: e.toShape() };
  }
}

/**
 * Apply an entitlement received from the server: persist it on the engine (which disables or re-enables
 * rows for the plan), then start or stop sync.
 */
export async function applyEntitlement(deps: SessionDeps, entitlement: Entitlement | null): Promise<SyncOutcome> {
  const now = (deps.now ?? Date.now)();
  await deps.engine.plan.setEntitlement(entitlement);
  if (syncAllowed(entitlement, now)) return startSync(deps);
  const status = await deps.engine.sync.status();
  if (status.phase !== 'off') await deps.engine.sync.stop();
  return { status: 'off' };
}

/** After any successful sign-in: remember the account, apply its plan, start sync when it has sync. */
async function signedIn(deps: SessionDeps, res: VerifyCodeResponse | SignInResponse | undefined): Promise<SessionState> {
  if (!res || typeof res.account !== 'object' || res.account === null) {
    throw new DigesterError('internal', 'The server sent an unexpected sign-in response');
  }
  await deps.engine.settings.setLocal('account', { email: res.account.email });
  const sync = await applyEntitlement(deps, res.account.entitlement);
  return { account: res.account, sync };
}

/** Sign in with the six-digit code (a new account, no password yet, a forgotten password, a pause). */
export async function signIn(deps: SessionDeps, email: string, code: string): Promise<SessionState> {
  const body: VerifyCodeRequest = { email: email.trim(), code: code.trim() };
  return signedIn(deps, await deps.api.call<VerifyCodeResponse>('verifyCode', { body }));
}

/** The normal sign-in: e-mail + password. The password is sent as typed (the server normalises it). */
export async function signInWithPassword(deps: SessionDeps, email: string, password: string): Promise<SessionState> {
  const body: SignInRequest = { email: email.trim(), password };
  return signedIn(deps, await deps.api.call<SignInResponse>('signIn', { body }));
}

/**
 * Set or change the account's password (session). The server ends every other session of the account;
 * this one — and this device's sync — carry on.
 */
export async function setPassword(api: ApiCaller, password: string, currentPassword?: string): Promise<SetPasswordResponse> {
  const body: SetPasswordRequest = currentPassword === undefined ? { password } : { password, currentPassword };
  return api.call<SetPasswordResponse>('setPassword', { body });
}

/**
 * Re-read the account from the server (boot, coming online, a timer, the end of a paid period).
 * Signed out locally → nothing. The server no longer knows the session → signed out locally, data kept.
 * Offline → the cached entitlement stays, except that an ended paid period is applied as Free.
 */
export async function refresh(deps: SessionDeps): Promise<SessionState> {
  const local = await deps.engine.settings.getLocal();
  if (!local.account) return { account: null, sync: { status: 'off' } };
  let me: MeResponse;
  try {
    me = await deps.api.call<MeResponse>('me');
  } catch (error) {
    const e = toDigesterError(error);
    if (e.code === 'unauthenticated') {
      await forgetAccount(deps);
      return { account: null, sync: { status: 'off' } };
    }
    const now = (deps.now ?? Date.now)();
    const cached = local.entitlement;
    if (cached && cached.plan !== 'free' && !isActivePaid(cached, now)) await applyEntitlement(deps, null);
    return { account: null, sync: { status: 'error', error: shape(e) } };
  }
  if (me.email !== local.account.email) await deps.engine.settings.setLocal('account', { email: me.email });
  const sync = await applyEntitlement(deps, me.entitlement);
  return { account: me, sync };
}

async function forgetAccount(deps: SessionDeps): Promise<void> {
  const status = await deps.engine.sync.status();
  if (status.phase !== 'off') await deps.engine.sync.stop();
  await deps.engine.settings.setLocal('account', null);
  await deps.engine.plan.setEntitlement(null);
}

/**
 * Sign out: this device's sync registration is revoked (so the same browser can later sync with
 * another account — the server never moves an ACTIVE device id), the server session ends, and local
 * data stays as a Free workspace (sync.md §11).
 */
export async function signOut(deps: SessionDeps): Promise<void> {
  try {
    const status = await deps.engine.sync.status();
    if (status.phase !== 'off') {
      const devices = await deps.engine.sync.devices();
      const self = devices.find((d) => d.current);
      if (self) await deps.engine.sync.revokeDevice(self.id);
    }
  } catch {
    // Offline or already revoked: signing out still proceeds.
  }
  try {
    await deps.api.call<OkResponse>('logout', { body: {} });
  } catch (error) {
    // Already signed out on the server, or offline: the local sign-out still happens.
    const e = toDigesterError(error);
    if (e.code !== 'unauthenticated' && e.code !== 'fetch_failed') throw e;
  }
  await forgetAccount(deps);
}
